/**
 * The Listing's terms as form fields, in the order a landlord thinks about them: Rent and
 * availability, Lease terms, Move-in costs, Utilities and insurance, then Policies (Pets, House
 * rules, Income limits). Nothing is collapsed. Rendered identically by the guide's Listing details
 * step and the live Listing editor; both feed it a `useListingTermsForm` instance.
 *
 * Jurisdiction guidance is soft (BR-1a): help text and warnings only, never a blocked save.
 */

import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import {
  ACCEPTED_LEASE_TERMS,
  LEASE_TERM_LABELS,
  UTILITY_TYPES,
  UTILITY_LABELS,
  FURNISHED_OPTIONS,
  FURNISHED_LABELS,
  type FurnishedOption,
  type ListingConstraints,
  type MaxPetSize,
  type PetType,
} from '../../../lib/leasing.api';
import {
  INCOME_SIZES,
  legalMoveInAgainstCap,
  toCents,
  type ListingTermsErrors,
  type ListingTermsForm,
  type TermsErrorKey,
} from './useListingTermsForm';
import { formatRentCents } from './shared';
import { DOLLAR_INPUT } from '../../../lib/dollar-input';

const PET_SPECIES: { value: PetType; label: string }[] = [
  { value: 'dogs', label: 'Dogs' },
  { value: 'cats', label: 'Cats' },
  { value: 'birds', label: 'Birds' },
  { value: 'reptiles', label: 'Reptiles' },
  { value: 'fish', label: 'Fish' },
  { value: 'other', label: 'Other' },
];

const MAX_PET_SIZE_OPTIONS: { value: MaxPetSize | ''; label: string }[] = [
  { value: '', label: '— Select —' },
  { value: 'all', label: 'All sizes' },
  { value: 'huge', label: 'Huge' },
  { value: 'large', label: 'Large' },
  { value: 'medium', label: 'Medium' },
  { value: 'small', label: 'Small' },
  { value: 'tiny', label: 'Tiny' },
];

const INPUT_CLASS =
  'w-full min-h-[44px] border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-destructive';

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function Card({ title, note, children, sectionId }: { title: string; note?: string; children: ReactNode; sectionId: string }) {
  const headingId = `${sectionId}-title`;
  return (
    <section aria-labelledby={headingId} data-testid={sectionId} className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div>
        <h3 id={headingId} className="text-base font-semibold">
          {title}
        </h3>
        {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
      </div>
      {children}
    </section>
  );
}

function Chip({
  type = 'checkbox',
  checked,
  onChange,
  'data-testid': testId,
  name,
  children,
}: {
  type?: 'checkbox' | 'radio';
  checked: boolean;
  onChange: (checked: boolean) => void;
  'data-testid': string;
  name?: string;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        'inline-flex min-h-[44px] cursor-pointer items-center rounded-full border px-4 text-sm transition-colors focus-within:ring-2 focus-within:ring-ring',
        checked ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input bg-background text-foreground hover:bg-accent'
      )}
    >
      <input
        type={type}
        name={name}
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      {children}
    </label>
  );
}

/** A single yes/no setting: a visible checkbox with its label (chips are for picking several from a set). */
function CheckboxField({
  checked,
  onChange,
  'data-testid': testId,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  'data-testid': string;
  children: ReactNode;
}) {
  return (
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-foreground">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 cursor-pointer rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {children}
    </label>
  );
}

function FieldError({ errors, field, testIdPrefix }: { errors: ListingTermsErrors; field: TermsErrorKey; testIdPrefix: string }) {
  const message = errors[field];
  if (!message) return null;
  return (
    <p role="alert" className="text-xs text-destructive mt-1" data-testid={`${testIdPrefix}error-${field}`}>
      {message}
    </p>
  );
}

function MoneyField({
  id,
  'data-testid': testId,
  label,
  value,
  onChange,
  help,
  error,
  placeholder = '0',
  required,
}: {
  id: string;
  'data-testid': string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  help?: ReactNode;
  error?: ReactNode;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(e) => {
          if (DOLLAR_INPUT.test(e.target.value)) onChange(e.target.value);
        }}
        placeholder={placeholder}
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        className={INPUT_CLASS}
      />
      {help}
      {error}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jurisdiction help (soft, BR-1a)
// ---------------------------------------------------------------------------

const HELP_CLASS = 'text-xs text-muted-foreground mt-0.5';

function SecurityDepositHelp({ constraints, rentCents, testIdPrefix }: { constraints: ListingConstraints | null; rentCents: number; testIdPrefix: string }) {
  if (!constraints || constraints.securityDeposit.maxMultiplier == null) return null;
  const cap = constraints.securityDeposit.maxMultiplier * rentCents;
  return (
    <p className={HELP_CLASS} data-testid={`${testIdPrefix}constraint-help-securityDepositCents`}>
      {constraints.jurisdiction.displayName} caps security deposit at {constraints.securityDeposit.maxMultiplier}× rent ({formatRentCents(cap)}).
    </p>
  );
}

function PetDepositHelp({ constraints, rentCents, testIdPrefix }: { constraints: ListingConstraints | null; rentCents: number; testIdPrefix: string }) {
  if (!constraints || constraints.securityDeposit.petDepositMaxMultiplier == null) return null;
  const cap = constraints.securityDeposit.petDepositMaxMultiplier * rentCents;
  return (
    <p className={HELP_CLASS} data-testid={`${testIdPrefix}constraint-help-petDepositCents`}>
      {constraints.jurisdiction.displayName} caps pet deposit at {constraints.securityDeposit.petDepositMaxMultiplier}× rent ({formatRentCents(cap)}).
    </p>
  );
}

function AppFeeHelp({ constraints, testIdPrefix }: { constraints: ListingConstraints | null; testIdPrefix: string }) {
  if (!constraints || constraints.applicationFee.maxCents == null) return null;
  return (
    <p className={HELP_CLASS} data-testid={`${testIdPrefix}constraint-help-applicationFeeCents`}>
      {constraints.jurisdiction.displayName} caps application fee at {formatRentCents(constraints.applicationFee.maxCents)}.
    </p>
  );
}

function MoveInCapHelp({ constraints, rentCents, testIdPrefix }: { constraints: ListingConstraints | null; rentCents: number; testIdPrefix: string }) {
  if (!constraints || constraints.moveInCost.maxFirstMonthMultiplier == null) return null;
  const cap = constraints.moveInCost.maxFirstMonthMultiplier * rentCents;
  return (
    <p className={HELP_CLASS} data-testid={`${testIdPrefix}constraint-help-moveInCost`}>
      {constraints.jurisdiction.displayName} caps legal move-in at {constraints.moveInCost.maxFirstMonthMultiplier}× rent ({formatRentCents(cap)}) — security deposit + last month&apos;s rent only. Application fee is excluded.
      {constraints.moveInCost.petDepositCountedInCeiling && (
        <span className="block">Pet deposit is counted toward this ceiling.</span>
      )}
    </p>
  );
}

function SoftWarning({ warningId, children }: { warningId: string; children: ReactNode }) {
  return (
    <p
      role="status"
      data-testid={warningId}
      className="rounded-md border border-warning/25 bg-warning/10 px-3 py-2 text-sm text-warning"
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// The fields
// ---------------------------------------------------------------------------

export interface ListingTermsFieldsProps {
  form: ListingTermsForm;
  /** The unit's jurisdiction constraints; null while unknown (no help or warnings then). */
  constraints: ListingConstraints | null;
  /** Used for `id`/`htmlFor`, e.g. 'create' or 'edit'. */
  idPrefix: string;
  /** Used for `data-testid`, e.g. 'create-listing-' or 'listing-edit-'. */
  testIdPrefix: string;
}

export function ListingTermsFields({ form, constraints, idPrefix, testIdPrefix: tp }: ListingTermsFieldsProps) {
  const { values, errors, set, setIncome } = form;
  const rentCents = toCents(values.askingRent) ?? 0;
  const depositMultiplier = constraints?.securityDeposit.maxMultiplier;
  const deposit = toCents(values.securityDeposit);
  const depositOverCap = rentCents > 0 && depositMultiplier != null && deposit != null && deposit > depositMultiplier * rentCents;
  const legal = legalMoveInAgainstCap(values, constraints);
  const moveInOverCap = legal != null && legal.totalCents > legal.capCents;
  // null (not an empty element) when the field is valid, so the input's aria-invalid reflects it.
  const errorFor = (field: TermsErrorKey) => (errors[field] ? <FieldError errors={errors} field={field} testIdPrefix={tp} /> : null);

  return (
    <div className="space-y-5">
      <Card title="Rent and availability" sectionId={`${tp}section-rent`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MoneyField
            id={`${idPrefix}-asking-rent`}
            data-testid={`${tp}asking-rent`}
            label="Asking rent per month ($)"
            value={values.askingRent}
            onChange={(v) => set('askingRent', v)}
            placeholder="2500"
            required
            error={errorFor('askingRent')}
          />
          <div>
            <label htmlFor={`${idPrefix}-available-date`} className="block text-sm font-medium mb-1">
              Available from
            </label>
            <input
              id={`${idPrefix}-available-date`}
              data-testid={`${tp}available-date`}
              type="date"
              value={values.availableDate}
              onChange={(e) => set('availableDate', e.target.value)}
              required
              aria-required="true"
              aria-invalid={errors.availableDate ? true : undefined}
              className={INPUT_CLASS}
            />
            {errorFor('availableDate')}
          </div>
        </div>
      </Card>

      <Card title="Lease terms you accept" note="Choose at least one." sectionId={`${tp}section-terms`}>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Accepted lease terms" data-testid={`${tp}lease-terms`}>
          {ACCEPTED_LEASE_TERMS.map((term) => (
            <Chip
              key={term}
              checked={values.acceptedLeaseTerms.includes(term)}
              onChange={() => form.toggleLeaseTerm(term)}
              data-testid={`${idPrefix}-lease-term-${term}`}
            >
              {LEASE_TERM_LABELS[term]}
            </Chip>
          ))}
        </div>
        {errorFor('acceptedLeaseTerms')}
      </Card>

      <Card title="Move-in costs" note="Everything a tenant pays up front." sectionId={`${tp}section-costs`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MoneyField
            id={`${idPrefix}-security-deposit`}
            data-testid={`${tp}security-deposit`}
            label="Security deposit ($)"
            value={values.securityDeposit}
            onChange={(v) => set('securityDeposit', v)}
            help={<SecurityDepositHelp constraints={constraints} rentCents={rentCents} testIdPrefix={tp} />}
            error={errorFor('securityDeposit')}
          />
          <MoneyField
            id={`${idPrefix}-application-fee`}
            data-testid={`${tp}application-fee`}
            label="Application fee ($)"
            value={values.applicationFee}
            onChange={(v) => set('applicationFee', v)}
            help={<AppFeeHelp constraints={constraints} testIdPrefix={tp} />}
            error={errorFor('applicationFee')}
          />
          <MoneyField
            id={`${idPrefix}-admin-fee`}
            data-testid="fee-administration"
            label="Administration fee ($)"
            value={values.adminFee}
            onChange={(v) => set('adminFee', v)}
            error={errorFor('adminFee')}
          />
          <MoneyField
            id={`${idPrefix}-holding-deposit`}
            data-testid="fee-holding-deposit"
            label="Holding deposit ($)"
            value={values.holdingDeposit}
            onChange={(v) => set('holdingDeposit', v)}
            error={errorFor('holdingDeposit')}
          />
          <MoneyField
            id={`${idPrefix}-parking-fee`}
            data-testid="fee-parking-monthly"
            label="Parking fee ($/month)"
            value={values.parkingFee}
            onChange={(v) => set('parkingFee', v)}
            error={errorFor('parkingFee')}
          />
        </div>

        <div className="flex flex-col">
          <CheckboxField checked={values.firstMonthRequired} onChange={(v) => set('firstMonthRequired', v)} data-testid={`${tp}first-month-required`}>
            First month&apos;s rent required
          </CheckboxField>
          <CheckboxField checked={values.lastMonthRequired} onChange={(v) => set('lastMonthRequired', v)} data-testid={`${tp}last-month-required`}>
            Last month&apos;s rent required
          </CheckboxField>
        </div>

        {depositOverCap && (
          <SoftWarning warningId={`${tp}security-deposit-over-cap`}>
            <strong>Security deposit is above the limit for this location.</strong> You can still save and publish. Check the local rules before you do.
          </SoftWarning>
        )}
        {moveInOverCap && legal && (
          <SoftWarning warningId={`${tp}move-in-over-cap`}>
            <strong>Legal move-in total is above the {formatRentCents(legal.capCents)} cap for this location.</strong> You can still save and publish. Check the local rules before you do.
          </SoftWarning>
        )}

        <div data-testid={`${tp}move-in-total`} className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
          <span>
            <span className="font-medium">Estimated move-in total</span>
            <span className="text-muted-foreground"> · first month, deposits and fees</span>
          </span>
          <span className="font-semibold" data-testid={`${tp}move-in-total-amount`}>{formatRentCents(form.moveInTotalCents)}</span>
        </div>
        <MoveInCapHelp constraints={constraints} rentCents={rentCents} testIdPrefix={tp} />
        {constraints?.securityDeposit.installmentRequiredAboveMultiplier != null && (
          <p data-testid={`${tp}installment-disclaimer`} className="text-xs text-warning">
            Tenants in {constraints.jurisdiction.displayName} may have the right to pay deposits and fees in installments (installment threshold: {constraints.securityDeposit.installmentRequiredAboveMultiplier}× rent).
          </p>
        )}
      </Card>

      <Card title="Utilities and insurance" note="What the rent covers, and what a tenant must carry." sectionId={`${tp}section-utilities`}>
        <fieldset>
          <legend className="block text-sm font-medium mb-2">Utilities included in rent</legend>
          <div className="flex flex-wrap gap-2">
            {UTILITY_TYPES.map((util) => (
              <Chip
                key={util}
                checked={values.utilitiesIncluded.includes(util)}
                onChange={() => form.toggleUtility(util)}
                data-testid={`${tp}utility-${util}`}
              >
                {UTILITY_LABELS[util]}
              </Chip>
            ))}
          </div>
        </fieldset>
        <CheckboxField
          checked={values.requireRentersInsurance}
          onChange={(v) => set('requireRentersInsurance', v)}
          data-testid={`${tp}require-renters-insurance`}
        >
          Renter&apos;s insurance required
        </CheckboxField>
      </Card>

      <div>
        <h3 className="text-base font-semibold">Policies</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Pets first, then the house rules and any income limits.</p>
      </div>

      <Card title="Pets" sectionId={`${tp}section-pets`}>
        <CheckboxField checked={values.petsAllowed} onChange={(v) => set('petsAllowed', v)} data-testid={`${tp}pets-allowed`}>
          Pets allowed
        </CheckboxField>
        {values.petsAllowed && (
          <>
            <fieldset>
              <legend className="block text-sm font-medium mb-2">Which pets</legend>
              <div className="flex flex-wrap gap-2">
                {PET_SPECIES.map((s) => (
                  <Chip
                    key={s.value}
                    checked={values.petTypesAllowed.includes(s.value)}
                    onChange={() => form.togglePetType(s.value)}
                    data-testid={`pet-type-${s.value}`}
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>
            </fieldset>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor={`${idPrefix}-max-pet-size`} className="block text-sm font-medium mb-1">
                  Largest pet size
                </label>
                <select
                  id={`${idPrefix}-max-pet-size`}
                  data-testid="max-pet-size"
                  value={values.maxPetSize}
                  onChange={(e) => set('maxPetSize', e.target.value as MaxPetSize | '')}
                  className={INPUT_CLASS}
                >
                  {MAX_PET_SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <MoneyField
                id={`${idPrefix}-pet-deposit`}
                data-testid={`${tp}pet-deposit`}
                label="Pet deposit ($)"
                value={values.petDeposit}
                onChange={(v) => set('petDeposit', v)}
                help={<PetDepositHelp constraints={constraints} rentCents={rentCents} testIdPrefix={tp} />}
                error={errorFor('petDeposit')}
              />
              <MoneyField
                id={`${idPrefix}-pet-rent`}
                data-testid={`${tp}pet-rent`}
                label="Pet rent ($/month)"
                value={values.petRent}
                onChange={(v) => set('petRent', v)}
                error={errorFor('petRent')}
              />
            </div>
          </>
        )}
      </Card>

      <Card title="House rules" sectionId={`${tp}section-rules`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:self-end">
            <CheckboxField checked={values.smokingAllowed} onChange={(v) => set('smokingAllowed', v)} data-testid={`${tp}smoking-allowed`}>
              Smoking allowed
            </CheckboxField>
          </div>
          <div>
            <label htmlFor={`${idPrefix}-furnished`} className="block text-sm font-medium mb-2">
              Furnishing
            </label>
            <select
              id={`${idPrefix}-furnished`}
              data-testid={`${tp}furnished`}
              value={values.furnished}
              onChange={(e) => set('furnished', e.target.value as FurnishedOption)}
              className={INPUT_CLASS}
            >
              {FURNISHED_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {FURNISHED_LABELS[opt]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card
        title="Income limits"
        note="Only for government-program or income-restricted units. A minimum monthly household income for each household size. Leave blank if there are none."
        sectionId={`${tp}section-income`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {INCOME_SIZES.map(({ key, label, testid }, index) => (
            <MoneyField
              key={key}
              id={`${idPrefix}-income-hh${index + 1}`}
              data-testid={testid}
              label={label}
              value={values.income[key]}
              onChange={(v) => setIncome(key, v)}
              error={errorFor(`income.${key}`)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}
